import { useMemo } from 'react';
import type { BuildState, BuildDerived } from '@/types';
import { getShaft } from '@/lib/calculations';
import { NOCKS } from '@/data/nocks';
import { INSERTS } from '@/data/inserts';
import { POINTS } from '@/data/points';
import { VANES } from '@/data/vanes';
import {
  summarizeBuildDrift,
  getVaneAirfoilTier,
} from '@/lib/windDrift';

interface SVGDiagramProps {
  state: BuildState;
  derived: BuildDerived;
}

// Layout constants
const VIEW_W = 800;
const VIEW_H = 200;
const PAD_L = 60;
const PAD_R = 60;
const ARROW_Y = 90;

function parseOD(od: string): number {
  const cleaned = od.replace(/[^0-9.]/g, '');
  const v = parseFloat(cleaned);
  return Number.isFinite(v) ? v : 0.234;
}

/**
 * Live arrow visualization. Renders shaft, vanes, nock, wrap, insert, point/broadhead,
 * the physical-center line and the CG (balance point) marker. Smoothly animates the
 * CG dot via CSS transitions on the `cx` attribute.
 */
export function SVGDiagram({ state, derived }: SVGDiagramProps) {
  const L = state.length;
  const shaft = getShaft(state);
  const nock = NOCKS[state.nockIdx] ?? NOCKS[0];
  const insert = INSERTS[state.insertIdx] ?? INSERTS[0];
  const point = POINTS[state.pointPreset] ?? POINTS[0];

  // X coordinate for an inch-along-arrow position
  const xAt = useMemo(() => {
    const usable = VIEW_W - PAD_L - PAD_R;
    return (inches: number) => PAD_L + (inches / L) * usable;
  }, [L]);

  const od = parseOD(shaft.od);
  const shaftPx = 4 + (od - 0.16) * 30; // 4–7 px tall — visually conveys diameter

  const isBroadhead =
    point.category === 'fixed-broadhead' || point.category === 'mechanical-broadhead';
  const isHeavyInsert = insert.weight > 30;

  const cx = xAt(L / 2); // physical center
  const bpx = xAt(derived.balancePoint); // balance point (CG)
  const focOffset = derived.balancePoint - L / 2;

  // ─────────────────────────────────────────────────────────────────
  // Fluid dynamics visualization — air pocket + spin indicator + airfoil
  // ─────────────────────────────────────────────────────────────────
  const vane = VANES[state.vaneIdx];
  const airfoilTier = getVaneAirfoilTier(vane);
  const driftSummary = useMemo(() => summarizeBuildDrift({
    vane,
    vaneCount: state.vaneCount,
    helicalDeg: state.helicalDeg ?? 2,
    velocityFps: state.fps,
  }), [vane, state.vaneCount, state.helicalDeg, state.fps]);

  // Air pocket halo: a translucent gradient envelope around the shaft.
  // Width-thickness scales with the build's drift coefficient.
  //   coeff ≈ 0.82 (Firenock AV3)   → 6 px tight halo  (best)
  //   coeff ≈ 1.00 (Blazer baseline) → 14 px halo
  //   coeff ≈ 1.10 (4-fletch large)  → 20 px wide halo (worst)
  const pocketThickness = Math.max(4, Math.min(28, (driftSummary.buildCoefficient - 0.6) * 50));
  const pocketIsAirfoil = airfoilTier === 'airfoil';
  // Airfoil-tier vanes get the ember/orange Sparrow color.
  // Standard / low-profile vanes get sky-blue.
  const pocketColor = pocketIsAirfoil ? '#f59e0b' : '#38bdf8';
  const pocketOpacity = pocketIsAirfoil ? 0.18 : 0.13;

  // Spin direction sign for visualization. Helical sign isn't tracked in
  // state today so we pick CW (right-hand spin) by convention — most
  // commercially-fletched arrows spin CW from the shooter's POV.
  const spinDir = 1;
  const spinRPS = driftSummary.spinRPS;
  const spinShown = spinRPS > 1;

  // Ticks at every inch, labels every 5"
  const ticks = useMemo(() => {
    const arr: Array<{ inch: number; isMajor: boolean }> = [];
    for (let i = 0; i <= Math.ceil(L); i++) {
      arr.push({ inch: i, isMajor: i % 5 === 0 });
    }
    return arr;
  }, [L]);

  // Vane angle offsets (3 or 4)
  const vaneDirs = state.vaneCount === 3 ? [-1, 1, -0.4] : [-1, 1, -0.4, 0.4];

  return (
    <svg viewBox={`0 -80 ${VIEW_W} ${VIEW_H + 140}`} className="w-full h-auto min-w-[700px]">
      <defs>
        <linearGradient id="shaftGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#52525b" />
          <stop offset="50%" stopColor="#a1a1aa" />
          <stop offset="100%" stopColor="#3f3f46" />
        </linearGradient>
        <linearGradient id="pointGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#27272a" />
          <stop offset="100%" stopColor="#fafafa" />
        </linearGradient>
        <radialGradient id="cgGlow">
          <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#f59e0b" stopOpacity="0" />
        </radialGradient>
        <filter id="nockGlow">
          <feGaussianBlur stdDeviation="2" />
        </filter>
        {/* Air pocket gradient — vertical fade from edge to center.
            Color + intensity driven by airfoil tier above. */}
        <linearGradient id="airPocket" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor={pocketColor} stopOpacity="0" />
          <stop offset="20%" stopColor={pocketColor} stopOpacity={pocketOpacity * 0.4} />
          <stop offset="50%" stopColor={pocketColor} stopOpacity={pocketOpacity} />
          <stop offset="80%" stopColor={pocketColor} stopOpacity={pocketOpacity * 0.4} />
          <stop offset="100%" stopColor={pocketColor} stopOpacity="0" />
        </linearGradient>
        <filter id="pocketBlur">
          <feGaussianBlur stdDeviation="1.4" />
        </filter>
      </defs>

      {/* ============================================================
          v125 — Aerospace treatment, background layer:
          NACA-style minor grid + streamlines flowing past the arrow.
          ============================================================ */}
      <defs>
        <pattern id="aeroGridMinor" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#171717" strokeWidth="0.3" />
        </pattern>
      </defs>
      <rect x={0} y={-80} width={VIEW_W} height={VIEW_H + 140} fill="url(#aeroGridMinor)" opacity={0.8} />

      {/* Streamlines: cyan reference flow past the geometry. Four above, four below.
          Compressed near the broadhead (stagnation), expanded behind fletching (wake). */}
      <g stroke="#22d3ee" fill="none" opacity={0.18} strokeWidth={0.55}>
        {[
          { y0: ARROW_Y - 50, yPeak: -7, yEnd: -5 },
          { y0: ARROW_Y - 38, yPeak: -5, yEnd: -3 },
          { y0: ARROW_Y - 28, yPeak: -3, yEnd: -1 },
          { y0: ARROW_Y - 20, yPeak: -1, yEnd: 0 },
          { y0: ARROW_Y + 20, yPeak: 1, yEnd: 0 },
          { y0: ARROW_Y + 28, yPeak: 3, yEnd: 1 },
          { y0: ARROW_Y + 38, yPeak: 5, yEnd: 3 },
          { y0: ARROW_Y + 50, yPeak: 7, yEnd: 5 },
        ].map((s, i) => (
          <path
            key={i}
            d={`M ${PAD_L - 50} ${s.y0}
                Q ${xAt(3)} ${s.y0 + s.yPeak * 2}
                  ${xAt(L * 0.4)} ${s.y0 + s.yPeak * 4}
                T ${xAt(L) + 30} ${s.y0 + s.yEnd}`}
          />
        ))}
      </g>

      {/* Air pocket / boundary layer around the shaft — rendered next so
          everything else paints on top. Size scales with drift coefficient. */}
      <rect
        x={xAt(0)}
        y={ARROW_Y - shaftPx / 2 - pocketThickness}
        width={xAt(L) - xAt(0)}
        height={shaftPx + pocketThickness * 2}
        fill="url(#airPocket)"
        filter="url(#pocketBlur)"
        rx={pocketThickness}
        pointerEvents="none"
      />

      {/* Component labels at top */}
      <text x={PAD_L + 2} y={20} textAnchor="start" fontSize={8} fill="#52525b"
        fontFamily="JetBrains Mono" letterSpacing="0.1em">NOCK</text>
      <text x={xAt(3)} y={20} textAnchor="middle" fontSize={8} fill="#52525b"
        fontFamily="JetBrains Mono" letterSpacing="0.1em">VANES</text>
      <text x={xAt(L * 0.55)} y={20} textAnchor="middle" fontSize={8} fill="#52525b"
        fontFamily="JetBrains Mono" letterSpacing="0.1em">SHAFT</text>
      <text x={xAt(L - 0.5)} y={20} textAnchor="middle" fontSize={8} fill="#52525b"
        fontFamily="JetBrains Mono" letterSpacing="0.1em">INSERT</text>
      <text x={xAt(L) + 18} y={20} textAnchor="end" fontSize={8} fill="#52525b"
        fontFamily="JetBrains Mono" letterSpacing="0.1em">POINT</text>

      {/* Total weight & velocity readout */}
      <text x={VIEW_W - PAD_R} y={ARROW_Y - 46} textAnchor="end" fontSize={10}
        fill="#71717a" fontFamily="JetBrains Mono">
        {derived.weight.toFixed(0)} gr · {state.fps} fps
      </text>

      {/* Physical center line */}
      <line x1={cx} y1={ARROW_Y - 40} x2={cx} y2={ARROW_Y + 36}
        stroke="#71717a" strokeWidth={1} strokeDasharray="3 3" />
      <text x={cx} y={ARROW_Y - 46} textAnchor="middle" fontSize={9} fill="#71717a"
        fontFamily="JetBrains Mono">CENTER</text>

      {/* Shaft body */}
      <rect
        x={xAt(0.2)}
        y={ARROW_Y - shaftPx / 2}
        width={xAt(L - 0.2) - xAt(0.2)}
        height={shaftPx}
        fill="url(#shaftGrad)"
        rx={shaftPx / 2}
      />

      {/* Wrap (conditional) */}
      {state.wrap && (
        <rect
          x={xAt(0.5)}
          y={ARROW_Y - shaftPx / 2 - 0.5}
          width={xAt(0.5 + state.wrapLen) - xAt(0.5)}
          height={shaftPx + 1}
          fill="#a78bfa"
          opacity={0.85}
          rx={1}
        />
      )}

      {/* Vanes — airfoil-tier (Firenock AV2/AV3) renders with a subtle ember
          glow to signal a different aerodynamic mechanism. Standard vanes
          render in classic orange. */}
      {vaneDirs.map((dir, i) => {
        const root = xAt(1.0);
        const tip = xAt(4.5);
        const yOff = dir * (shaftPx / 2 + 14);
        const isAirfoilVane = airfoilTier === 'airfoil';
        return (
          <g key={i}>
            {isAirfoilVane && (
              <path
                d={`M ${root} ${ARROW_Y} Q ${(root + tip) / 2} ${ARROW_Y + yOff} ${tip} ${ARROW_Y}`}
                fill="none"
                stroke="#f59e0b"
                strokeWidth={3}
                opacity={0.35}
                filter="url(#pocketBlur)"
              />
            )}
            <path
              d={`M ${root} ${ARROW_Y} Q ${(root + tip) / 2} ${ARROW_Y + yOff} ${tip} ${ARROW_Y}`}
              fill={isAirfoilVane ? '#fbbf24' : '#fb923c'}
              opacity={0.4 + Math.abs(dir) * 0.4}
              stroke={isAirfoilVane ? '#fbbf24' : '#fb923c'}
              strokeWidth={1}
            />
          </g>
        );
      })}

      {/* Spin indicator — curved arrow + RPS label near the vanes.
          Only rendered when there's actual spin (helical > 0 or airfoil). */}
      {spinShown && (() => {
        const cxSpin = xAt(2.75);
        const r = shaftPx / 2 + 7;
        const startAngle = -50;
        const endAngle = 50;
        const toRad = (a: number) => (a * Math.PI) / 180;
        const x1 = cxSpin + r * Math.cos(toRad(startAngle));
        const y1 = ARROW_Y + r * Math.sin(toRad(startAngle));
        const x2 = cxSpin + r * Math.cos(toRad(endAngle));
        const y2 = ARROW_Y + r * Math.sin(toRad(endAngle));
        const tipX = x2 + 3 * spinDir;
        const tipY = y2 + (spinDir > 0 ? 0.5 : -0.5);
        const arrowColor = airfoilTier === 'airfoil' ? '#fbbf24' : '#a1a1aa';
        return (
          <g opacity={Math.min(0.9, 0.4 + spinRPS / 600)}>
            <path
              d={`M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2}`}
              fill="none"
              stroke={arrowColor}
              strokeWidth={1.2}
              strokeLinecap="round"
            />
            <polygon
              points={`${tipX},${tipY} ${x2 - 2.5},${y2 - 3 * spinDir} ${x2 + 1},${y2 - 2 * spinDir}`}
              fill={arrowColor}
            />
            <text
              x={cxSpin}
              y={ARROW_Y - r - 6}
              textAnchor="middle"
              fontSize={8}
              fill={arrowColor}
              fontFamily="JetBrains Mono"
              letterSpacing="0.05em"
            >
              {Math.round(spinRPS)} RPS
            </text>
          </g>
        );
      })()}

      {/* Nock — body protrudes from back of shaft + two prongs forming a U-notch
          where the bowstring sits. Notch opens LEFT (toward shooter). */}
      {(() => {
        const color = nock.lighted ? '#f43f5e' : '#71717a';
        const halfH = shaftPx / 2;
        const bodyW = 6;   // body protrudes 6px past back of shaft
        const prongW = 8;  // prongs extend an additional 8px back
        const throatH = 1.5; // half-height of the string slot opening
        return (
          <g>
            {/* Body — small bushing extending past back of shaft */}
            <rect
              x={PAD_L - bodyW}
              y={ARROW_Y - halfH - 1.5}
              width={bodyW}
              height={shaftPx + 3}
              fill={color}
              stroke="#fafafa"
              strokeWidth={0.4}
              rx={0.5}
            />
            {/* Top prong — extends back-left, tapers down to the throat */}
            <path
              d={`
                M ${PAD_L - bodyW} ${ARROW_Y - halfH - 1.5}
                L ${PAD_L - bodyW - prongW} ${ARROW_Y - halfH - 3}
                L ${PAD_L - bodyW - prongW + 2} ${ARROW_Y - throatH}
                L ${PAD_L - bodyW} ${ARROW_Y - throatH}
                Z
              `}
              fill={color}
              stroke="#fafafa"
              strokeWidth={0.4}
              strokeLinejoin="round"
            />
            {/* Bottom prong — mirror */}
            <path
              d={`
                M ${PAD_L - bodyW} ${ARROW_Y + halfH + 1.5}
                L ${PAD_L - bodyW - prongW} ${ARROW_Y + halfH + 3}
                L ${PAD_L - bodyW - prongW + 2} ${ARROW_Y + throatH}
                L ${PAD_L - bodyW} ${ARROW_Y + throatH}
                Z
              `}
              fill={color}
              stroke="#fafafa"
              strokeWidth={0.4}
              strokeLinejoin="round"
            />
            {/* Lighted nock glow */}
            {nock.lighted && (
              <circle
                cx={PAD_L - bodyW - 2}
                cy={ARROW_Y}
                r={8}
                fill="#f43f5e"
                opacity={0.55}
                filter="url(#nockGlow)"
              />
            )}
          </g>
        );
      })()}

      {/* Insert (color hints at material weight) */}
      <rect
        x={xAt(L - 1.0)}
        y={ARROW_Y - shaftPx / 2 - 0.5}
        width={xAt(L - 0.2) - xAt(L - 1.0)}
        height={shaftPx + 1}
        fill={isHeavyInsert ? '#fbbf24' : '#94a3b8'}
        rx={1}
      />

      {/* Point */}
      <path
        d={`M ${xAt(L)} ${ARROW_Y - shaftPx / 2 - 1} L ${xAt(L) + 18} ${ARROW_Y} L ${xAt(L)} ${ARROW_Y + shaftPx / 2 + 1} Z`}
        fill="url(#pointGrad)"
        stroke="#71717a"
        strokeWidth={0.5}
      />

      {/* Broadhead blades */}
      {isBroadhead && (
        <>
          <path
            d={`M ${xAt(L) + 4} ${ARROW_Y} L ${xAt(L) + 14} ${ARROW_Y - 10} L ${xAt(L) + 10} ${ARROW_Y} Z`}
            fill="#fafafa"
            opacity={0.7}
          />
          <path
            d={`M ${xAt(L) + 4} ${ARROW_Y} L ${xAt(L) + 14} ${ARROW_Y + 10} L ${xAt(L) + 10} ${ARROW_Y} Z`}
            fill="#fafafa"
            opacity={0.7}
          />
        </>
      )}

      {/* CG glow + dot */}
      <circle cx={bpx} cy={ARROW_Y} r={14} fill="url(#cgGlow)"
        style={{ transition: 'cx 200ms ease-out' }} />
      <circle cx={bpx} cy={ARROW_Y} r={8} fill="#f59e0b" className="pulse-dot"
        style={{ transition: 'cx 200ms ease-out' }} />
      <circle cx={bpx} cy={ARROW_Y} r={3} fill="#fafafa"
        style={{ transition: 'cx 200ms ease-out' }} />
      <text x={bpx} y={ARROW_Y - 22} textAnchor="middle" fontSize={10} fill="#f59e0b"
        fontFamily="JetBrains Mono" fontWeight="bold"
        style={{ transition: 'x 200ms ease-out' }}>
        CG {derived.balancePoint.toFixed(2)}"
      </text>

      {/* FOC offset arrow */}
      {Math.abs(focOffset) > 0.05 && (
        <>
          <path
            d={`M ${cx} ${ARROW_Y + 22} L ${bpx} ${ARROW_Y + 22}`}
            stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="2 2"
            style={{ transition: 'd 200ms ease-out' }}
          />
          <polygon
            points={`${bpx},${ARROW_Y + 22} ${bpx - (bpx > cx ? 5 : -5)},${ARROW_Y + 19} ${bpx - (bpx > cx ? 5 : -5)},${ARROW_Y + 25}`}
            fill="#f59e0b"
          />
          <text
            x={(cx + bpx) / 2}
            y={ARROW_Y + 34}
            textAnchor="middle"
            fontSize={9}
            fill="#f59e0b"
            fontFamily="JetBrains Mono"
          >
            {derived.foc.toFixed(1)}% FOC
          </text>
        </>
      )}

      {/* Ruler */}
      <line x1={PAD_L} y1={ARROW_Y + 40} x2={VIEW_W - PAD_R} y2={ARROW_Y + 40}
        stroke="#3f3f46" strokeWidth={1} />
      {ticks.map(({ inch, isMajor }) => {
        const x = xAt(inch);
        return (
          <g key={inch}>
            <line
              x1={x}
              y1={ARROW_Y + 40}
              x2={x}
              y2={ARROW_Y + (isMajor ? 46 : 44)}
              stroke="#52525b"
              strokeWidth={1}
            />
            {isMajor && (
              <text
                x={x}
                y={ARROW_Y + 58}
                textAnchor="middle"
                fontSize={10}
                fill="#71717a"
                fontFamily="JetBrains Mono"
              >
                {inch}"
              </text>
            )}
          </g>
        );
      })}
      {/* ============================================================
          v125 — Aerospace overlay layer:
          DETAIL A inset (insert+collar+point joint, 3:1 magnification)
          + dimension chains below the ruler
          + coordinate frame glyph
          ============================================================ */}

      {/* DETAIL A — callout circle around the insert/point joint */}
      <g>
        <circle
          cx={xAt(L) - 4}
          cy={ARROW_Y}
          r={22}
          fill="none"
          stroke="#22d3ee"
          strokeWidth={0.6}
          strokeDasharray="6 3"
          opacity={0.85}
        />
        <text
          x={xAt(L) - 4}
          y={ARROW_Y + 36}
          textAnchor="middle"
          fontSize={8}
          fill="#22d3ee"
          fontFamily="JetBrains Mono"
          letterSpacing="0.2em"
        >
          DETAIL A
        </text>
        {/* Leader line from callout to inset frame top-right */}
        <line
          x1={xAt(L) + 12}
          y1={ARROW_Y - 12}
          x2={645}
          y2={-30}
          stroke="#22d3ee"
          strokeWidth={0.5}
          strokeDasharray="3 2"
          opacity={0.7}
        />
      </g>

      {/* DETAIL A inset frame — top-right corner, magnified view of insert + collar + point joint */}
      <g transform="translate(540, -72)">
        <rect width={240} height={64} fill="#020617" stroke="#27272a" strokeWidth={0.6} />
        <rect x={3} y={3} width={234} height={58} fill="none" stroke="#171717" strokeWidth={0.3} />

        <text x={6} y={11} fill="#22d3ee" fontSize={8} fontFamily="JetBrains Mono" letterSpacing="0.2em">
          DETAIL A · 3:1
        </text>
        <text x={234} y={11} textAnchor="end" fill="#71717a" fontSize={8} fontFamily="JetBrains Mono" letterSpacing="0.1em">
          INSERT · COLLAR · POINT
        </text>

        {/* Magnified geometry — 3× scale view of last 1.5" of arrow */}
        <g transform="translate(20, 38)">
          {/* shaft tail */}
          <rect x={0} y={-6} width={80} height={12} fill="url(#shaftGrad)" rx={2} />
          {/* insert */}
          <rect x={80} y={-7} width={40} height={14} fill={isHeavyInsert ? '#fbbf24' : '#94a3b8'} stroke="#fafafa" strokeWidth={0.4} />
          {/* collar (visible band) */}
          <rect x={118} y={-8} width={6} height={16} fill="#fb923c" opacity={0.85} />
          {/* point base */}
          <path d={`M 124 -7 L 160 0 L 124 7 Z`} fill="url(#pointGrad)" stroke="#71717a" strokeWidth={0.4} />
          {/* broadhead blade if applicable */}
          {isBroadhead && (
            <>
              <path d={`M 130 0 L 158 -10 L 154 0 Z`} fill="#fafafa" opacity={0.7} />
              <path d={`M 130 0 L 158 10 L 154 0 Z`} fill="#fafafa" opacity={0.7} />
            </>
          )}
          {/* dimension callouts inside inset */}
          <line x1={80} y1={-14} x2={124} y2={-14} stroke="#22d3ee" strokeWidth={0.4} />
          <line x1={80} y1={-12} x2={80} y2={-15} stroke="#22d3ee" strokeWidth={0.4} />
          <line x1={124} y1={-12} x2={124} y2={-15} stroke="#22d3ee" strokeWidth={0.4} />
          <text x={102} y={-17} textAnchor="middle" fontSize={6} fill="#22d3ee" fontFamily="JetBrains Mono">
            {insert.weight} gr
          </text>
          <text x={102} y={20} textAnchor="middle" fontSize={6} fill="#71717a" fontFamily="JetBrains Mono">
            {insert.name.slice(0, 24)}
          </text>
        </g>
      </g>

      {/* Dimension chains below the ruler — OVERALL LENGTH + BALANCE OFFSET (when meaningful) */}
      <g fontFamily="JetBrains Mono">
        {/* OVERALL LENGTH dimension — full shaft span */}
        <g stroke="#22d3ee" strokeWidth={0.5}>
          {/* extension lines */}
          <line x1={xAt(0)} y1={ARROW_Y + 70} x2={xAt(0)} y2={ARROW_Y + 84} strokeDasharray="1 3" opacity={0.7} />
          <line x1={xAt(L)} y1={ARROW_Y + 70} x2={xAt(L)} y2={ARROW_Y + 84} strokeDasharray="1 3" opacity={0.7} />
          {/* dimension line */}
          <line x1={xAt(0)} y1={ARROW_Y + 82} x2={xAt(L)} y2={ARROW_Y + 82} opacity={0.9} />
          {/* arrowheads */}
          <polygon points={`${xAt(0)},${ARROW_Y + 82} ${xAt(0) + 5},${ARROW_Y + 80} ${xAt(0) + 5},${ARROW_Y + 84}`} fill="#22d3ee" />
          <polygon points={`${xAt(L)},${ARROW_Y + 82} ${xAt(L) - 5},${ARROW_Y + 80} ${xAt(L) - 5},${ARROW_Y + 84}`} fill="#22d3ee" />
        </g>
        <rect x={(xAt(0) + xAt(L)) / 2 - 20} y={ARROW_Y + 77} width={40} height={10} fill="#09090b" />
        <text x={(xAt(0) + xAt(L)) / 2} y={ARROW_Y + 85} textAnchor="middle" fontSize={9} fill="#22d3ee">
          {L.toFixed(2)}"
        </text>

        {/* BALANCE OFFSET dimension — physical center to CG, only when FOC > minimum threshold */}
        {Math.abs(focOffset) > 0.05 && (
          <>
            <g stroke="#f59e0b" strokeWidth={0.5}>
              <line x1={cx} y1={ARROW_Y + 70} x2={cx} y2={ARROW_Y + 105} strokeDasharray="1 3" opacity={0.7} />
              <line x1={bpx} y1={ARROW_Y + 70} x2={bpx} y2={ARROW_Y + 105} strokeDasharray="1 3" opacity={0.7} />
              <line x1={cx} y1={ARROW_Y + 102} x2={bpx} y2={ARROW_Y + 102} opacity={0.9} />
              <polygon points={`${cx},${ARROW_Y + 102} ${cx + 4 * Math.sign(bpx - cx)},${ARROW_Y + 100} ${cx + 4 * Math.sign(bpx - cx)},${ARROW_Y + 104}`} fill="#f59e0b" />
              <polygon points={`${bpx},${ARROW_Y + 102} ${bpx - 4 * Math.sign(bpx - cx)},${ARROW_Y + 100} ${bpx - 4 * Math.sign(bpx - cx)},${ARROW_Y + 104}`} fill="#f59e0b" />
            </g>
            <rect x={(cx + bpx) / 2 - 30} y={ARROW_Y + 97} width={60} height={10} fill="#09090b" />
            <text x={(cx + bpx) / 2} y={ARROW_Y + 105} textAnchor="middle" fontSize={8} fill="#f59e0b">
              {focOffset.toFixed(2)}" · {derived.foc.toFixed(1)}% FOC
            </text>
          </>
        )}
      </g>

      {/* Coordinate frame glyph — bottom-left corner. X·FLIGHT (right), Z·VERTICAL (down). */}
      <g transform={`translate(20, ${ARROW_Y + 100})`}>
        <line x1={0} y1={0} x2={16} y2={0} stroke="#52525b" strokeWidth={0.7} />
        <polygon points="16,0 12,-2 12,2" fill="#52525b" />
        <line x1={0} y1={0} x2={0} y2={-16} stroke="#52525b" strokeWidth={0.7} />
        <polygon points="0,-16 -2,-12 2,-12" fill="#52525b" />
        <text x={20} y={3} fontSize={7} fill="#71717a" fontFamily="JetBrains Mono" letterSpacing="0.1em">
          X · FLIGHT
        </text>
        <text x={-2} y={-19} textAnchor="end" fontSize={7} fill="#71717a" fontFamily="JetBrains Mono" letterSpacing="0.1em">
          Z · VERT
        </text>
      </g>

      {/* Page corner stamp — engineering document convention */}
      <text x={VIEW_W - 6} y={ARROW_Y + 116} textAnchor="end" fontSize={7} fill="#3f3f46" fontFamily="JetBrains Mono" letterSpacing="0.2em">
        SE-FLT-LIVE · REV B · v125
      </text>
    </svg>
  );
}
